import React, { useState, useEffect, useCallback } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { AppTextInput } from "@/components/ui/TextInput";
import { AppTextArea } from "@/components/ui/AppTextArea";
import { Button } from "@/components/ui/Button";
import { Modal as UIModal } from "@/components/ui/Modal";
import type { EditFieldMeta } from "./Shared";

export interface EditFieldModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: (newValue: string) => void;
  fieldMeta?: EditFieldMeta;
  /** Pre-filled value for the "New Value" input — used when re-opening to update an existing edit. */
  initialValue?: string;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
}

export function EditFieldModal({
  visible,
  onDismiss,
  onConfirm,
  fieldMeta,
  initialValue = "",
  t,
}: EditFieldModalProps) {
  const [newValue, setNewValue] = useState(initialValue);

  useEffect(() => {
    if (visible) setNewValue(initialValue);
  }, [visible, initialValue]);

  const handleDismiss = useCallback(() => {
    setNewValue(initialValue);
    onDismiss();
  }, [initialValue, onDismiss]);

  const handleConfirm = useCallback(() => {
    const value = newValue;
    setNewValue(initialValue);
    onConfirm(value);
  }, [newValue, initialValue, onConfirm]);

  return (
    <UIModal
      visible={visible}
      onDismiss={handleDismiss}
      title="review.review.editFieldModal.title"
      closeOnBackdrop={false}
      useScrollView
      footer={
        <View className="flex-row items-center justify-end gap-4">
          <Button label="common.cancel" variant="tertiary" onPress={handleDismiss} />
          <Button
            label="review.review.editFieldModal.saveAccept"
            variant="destructive"
            onPress={handleConfirm}
          />
        </View>
      }
    >
      {/* Field label subtitle */}
      {fieldMeta?.label && (
        <AppText weight="semibold" className="text-base text-dark-text-50 mb-4">
          {fieldMeta.label}
        </AppText>
      )}

      {/* Grey container holding both value fields */}
      <View className="bg-stroke-10 rounded-lg p-4 gap-5 w-full">
        {/* Current Value — read-only */}
        {fieldMeta?.multiline ? (
          <AppTextArea
            label="review.review.editFieldModal.currentValue"
            value={fieldMeta?.currentValue ?? ""}
            editable={false}
            containerClassName="mb-0"
          />
        ) : (
          <AppTextInput
            label="review.review.editFieldModal.currentValue"
            value={fieldMeta?.currentValue ?? ""}
            editable={false}
            containerClassName="mb-0"
          />
        )}

        {/* New Value — editable */}
        {fieldMeta?.multiline ? (
          <AppTextArea
            label="review.review.editFieldModal.newValue"
            value={newValue}
            onChangeText={setNewValue}
            containerClassName="mb-0"
          />
        ) : (
          <AppTextInput
            label="review.review.editFieldModal.newValue"
            value={newValue}
            onChangeText={setNewValue}
            containerClassName="mb-0"
          />
        )}
      </View>
    </UIModal>
  );
}
